if (!customElements.get('superior-variant-images')) {
  class SuperiorVariantImages extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.card = this.closest('[data-js-product-item]');
      if (!this.card) return;
      this.buttons = [...this.querySelectorAll('[data-superior-card-variant-button]')];
      this.buttons.forEach(button => {
        button.addEventListener('click', () => this.selectVariant(button));
      });
      this.addEventListener('keydown', event => this.handleKeyboard(event));
      const selectedButton = this.buttons.find(button => button.getAttribute('aria-checked') === 'true' && !button.disabled);
      const firstAvailableButton = this.buttons.find(button => !button.disabled);
      this.selectVariant(selectedButton || firstAvailableButton, false);
    }

    handleKeyboard(event) {
      const currentButton = event.target.closest?.('[data-superior-card-variant-button]');
      const availableButtons = this.buttons.filter(button => !button.disabled);
      const currentIndex = availableButtons.indexOf(currentButton);
      if (currentIndex < 0) return;

      let nextIndex = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % availableButtons.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + availableButtons.length) % availableButtons.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = availableButtons.length - 1;
      if (nextIndex === null) return;

      event.preventDefault();
      const nextButton = availableButtons[nextIndex];
      nextButton.focus();
      this.selectVariant(nextButton);
    }

    selectVariant(button, announce = true) {
      if (!button || button.disabled) return;
      this.buttons.forEach(option => {
        option.setAttribute('aria-checked', option === button ? 'true' : 'false');
        option.tabIndex = option === button ? 0 : -1;
      });

      const primaryImage = this.card.querySelector('.product-item__image-figure--primary img');
      if (primaryImage) {
        primaryImage.src = button.dataset.imageSrc;
        primaryImage.srcset = button.dataset.imageSrcset;
        primaryImage.alt = button.dataset.imageAlt;
        primaryImage.closest('figure')?.classList.add('lazyloaded');
        this.card.classList.add('superior-variant-selected');
      }

      const variantUrl = button.dataset.variantUrl;
      const variantId = button.dataset.variantId;
      [
        this.card.querySelector('.product-item__image'),
        this.card.querySelector('.product-item__title')
      ].filter(Boolean).forEach(link => {
        const linkUrl = new URL(link.href || variantUrl, window.location.origin);
        linkUrl.searchParams.set('variant', variantId);
        link.href = linkUrl.toString();
      });

      const quickBuyLink = this.card.querySelector('.product-item__quick-buy a');
      if (quickBuyLink) {
        const quickBuyUrl = new URL(quickBuyLink.href || variantUrl, window.location.origin);
        quickBuyUrl.searchParams.set('variant', variantId);
        quickBuyLink.href = quickBuyUrl.toString();
      }

      const quickBuyInput = this.card.querySelector('.product-item__quick-buy input[name="id"]');
      if (quickBuyInput) quickBuyInput.value = variantId;

      const quickView = this.card.querySelector('quick-view-product');
      if (quickView?.quickViewModal) {
        quickView.quickViewModal.remove();
        quickView.quickViewModal = null;
      }

      const priceTemplate = [...this.querySelectorAll('[data-superior-card-price]')]
        .find(template => template.dataset.superiorCardPrice === variantId);
      const priceTarget = this.card.querySelector('.product-item__price');
      const priceContent = priceTarget?.querySelector('.remove-line-height-space') || priceTarget;
      if (priceTemplate && priceContent) priceContent.innerHTML = priceTemplate.innerHTML;

      const status = this.querySelector('[data-superior-card-variant-status]');
      if (announce && status) {
        const priceText = priceTemplate?.content?.textContent || priceTemplate?.textContent || '';
        status.textContent = button.dataset.variantTitle + ' — ' + priceText.trim();
      }
    }
  }

  customElements.define('superior-variant-images', SuperiorVariantImages);
}

function syncSuperiorProductVariantLabel(input) {
  const option = input?.closest('[data-js-product-variant]');
  const label = option?.querySelector('.product-variant__name');
  if (!label) return;
  if (!label.dataset.superiorOptionName) {
    label.dataset.superiorOptionName = label.textContent.trim().replace(/:\s*$/, '');
  }
  label.classList.add('superior-product-variant__name');
  label.textContent = label.dataset.superiorOptionName + ': ' + input.value;
}

function syncSuperiorProductVariantMedia(input, behavior = 'auto') {
  if (!input?.dataset.superiorMediaId) return;

  const productComponent = input.closest('[data-js-product-component]');
  const gallery = productComponent?.querySelector('[data-js-product-gallery]');
  const media = gallery?.querySelector(
    `.product-gallery-item[data-media-id="${input.dataset.superiorMediaId}"]`
  );
  const slider = gallery?.querySelector('css-slider');
  if (!media || !slider) return;

  const changeSlide = () => {
    if (typeof slider.changeSlide !== 'function' || !slider.sliderEnabled) return false;
    slider.changeSlide(Number(media.dataset.index), behavior);
    return true;
  };

  if (changeSlide()) return;

  slider.addEventListener('ready', changeSlide, { once: true });
  window.setTimeout(changeSlide, 250);
}

function initializeSuperiorProductVariantMedia(root = document) {
  root.querySelectorAll('product-variants').forEach(variants => {
    const selectedInput = variants.querySelector('input[data-superior-media-id]:checked');
    if (selectedInput) {
      syncSuperiorProductVariantLabel(selectedInput);
      window.requestAnimationFrame(() => syncSuperiorProductVariantMedia(selectedInput));
    }
  });
}

document.addEventListener('change', event => {
  const input = event.target.closest?.('input[data-superior-media-id]');
  if (!input?.checked) return;
  syncSuperiorProductVariantLabel(input);
  window.requestAnimationFrame(() => syncSuperiorProductVariantMedia(input, 'smooth'));
});

if (document.readyState === 'complete') {
  initializeSuperiorProductVariantMedia();
} else {
  window.addEventListener('load', () => initializeSuperiorProductVariantMedia(), { once: true });
}

document.addEventListener('shopify:section:load', event => {
  initializeSuperiorProductVariantMedia(event.target);
});

function initializeSuperiorStickyAvailability(root = document) {
  const stickyBars = [];
  if (root.matches?.('sticky-add-to-cart[data-id]')) stickyBars.push(root);
  stickyBars.push(...root.querySelectorAll?.('sticky-add-to-cart[data-id]') || []);

  stickyBars.forEach(stickyBar => {
    if (stickyBar.dataset.superiorAvailabilitySync === 'true') return;

    const productRoot = document.getElementById(stickyBar.dataset.id);
    const stickyButton = stickyBar.querySelector('button[data-js-atc]');
    if (!productRoot || !stickyButton) return;

    const synchronize = () => {
      const productButton = productRoot.querySelector('[data-js-product-add-to-cart]');
      if (!productButton) return;

      const wasForcedDisabled =
        productButton.dataset.superiorAvailabilityDisabled === 'true';
      let isUnavailable = productButton.classList.contains('disabled');

      if (!isUnavailable && !wasForcedDisabled) {
        isUnavailable =
          productButton.disabled ||
          productButton.getAttribute('aria-disabled') === 'true';
      }

      if (isUnavailable) {
        if (!productButton.disabled) productButton.disabled = true;
        if (productButton.getAttribute('aria-disabled') !== 'true') {
          productButton.setAttribute('aria-disabled', 'true');
        }
        if (!wasForcedDisabled) {
          productButton.dataset.superiorAvailabilityDisabled = 'true';
        }
      } else if (wasForcedDisabled) {
        productButton.disabled = false;
        productButton.removeAttribute('aria-disabled');
        delete productButton.dataset.superiorAvailabilityDisabled;
      }

      const productButtonText = productButton.querySelector(
        '[data-js-product-add-to-cart-text], .button__text'
      );
      const stickyButtonText = stickyButton.querySelector('.button__text');

      if (productButtonText && stickyButtonText) {
        stickyButtonText.textContent = productButtonText.textContent.trim();
      }

      stickyButton.disabled = isUnavailable;
      stickyButton.classList.toggle('disabled', isUnavailable);

      if (isUnavailable) {
        stickyButton.setAttribute('aria-disabled', 'true');
      } else {
        stickyButton.removeAttribute('aria-disabled');
      }
    };

    stickyBar.dataset.superiorAvailabilitySync = 'true';
    synchronize();

    const observer = new MutationObserver(synchronize);
    observer.observe(productRoot, {
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-disabled'],
      childList: true,
      subtree: true
    });
  });
}

if (document.readyState === 'complete') {
  initializeSuperiorStickyAvailability();
} else {
  window.addEventListener('load', () => initializeSuperiorStickyAvailability(), { once: true });
}

document.addEventListener('shopify:section:load', event => {
  initializeSuperiorStickyAvailability(event.target);
});
